import { Button } from "./ui/button";
import { Phone } from "lucide-react";
import { motion, AnimatePresence, useScroll } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { useCookieConsentContext } from "@/contexts/CookieConsentContext";

export const StickyCTA = () => {
  const [isVisible, setIsVisible] = useState(false);
  const { scrollY } = useScroll();
  const { showBanner } = useCookieConsentContext();

  // Cache layout dimensions in refs to avoid forced reflow on every scroll tick
  const windowHeightRef = useRef(0);
  const scrollHeightRef = useRef(0);

  // Update cached dimensions on mount and resize only
  useEffect(() => {
    const updateDimensions = () => {
      windowHeightRef.current = window.innerHeight;
      scrollHeightRef.current = document.documentElement.scrollHeight;
    };

    updateDimensions();
    window.addEventListener("resize", updateDimensions);
    return () => window.removeEventListener("resize", updateDimensions);
  }, []);

  useEffect(() => {
    const unsubscribe = scrollY.on("change", (latest) => {
      // Read from cached refs instead of triggering layout recalculation
      const wh = windowHeightRef.current;
      const sh = scrollHeightRef.current;

      // Show sticky CTA after scrolling past hero (approximately 75vh)
      const showAfterHero = latest > wh * 0.75;
      
      // Hide when near bottom of page (footer area)
      const scrollPosition = latest + wh;
      const hideBeforeBottom = scrollPosition < sh - 100;
      
      setIsVisible(showAfterHero && hideBeforeBottom);
    });

    return () => unsubscribe();
  }, [scrollY]);

  const scrollToContact = () => {
    const element = document.getElementById("contact");
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  // Hide the StickyCTA when the cookie banner is visible to prevent overlap
  const shouldShow = isVisible && !showBanner;

  return (
    <AnimatePresence>
      {shouldShow && (
        <motion.div
          key="sticky-cta"
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          className="fixed bottom-0 left-0 right-0 z-50 bg-navy-900/95 backdrop-blur-md border-t border-white/10 shadow-2xl"
        >
          <div className="container mx-auto px-4 py-4">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-center sm:text-left hidden sm:block">
                <p className="text-white font-bold text-sm sm:text-base">
                  Ready to upgrade your boat's windows?
                </p>
                <p className="text-seafoam-300 text-xs sm:text-sm">
                  Free quotes • Expert installation • San Diego
                </p>
              </div>
              <div className="flex gap-3 w-full sm:w-auto">
                <Button
                  size="lg"
                  onClick={scrollToContact}
                  className="bg-white hover:bg-white/90 text-navy-900 hover:text-navy-800 font-bold shadow-lg hover:shadow-xl transition-all flex-1 sm:flex-none touch-target min-h-[48px]"
                >
                  Request Quote
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  asChild
                  className="bg-transparent hover:bg-white/10 border-2 border-white/80 hover:border-white text-white font-semibold flex-1 sm:flex-none touch-target min-h-[48px]"
                >
                  <a href="tel:6193774673" className="flex items-center justify-center">
                    <Phone className="mr-2 h-5 w-5" />
                    Call Now
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
